if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this), { capture: true });
        this.addEventListener('click', this.onDynamicCheckoutClick.bind(this), { capture: true });
        this._dynamicCheckoutAllowed = false;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const productId = this.dataset.productId;

        this._fetchStockStatus(productId)
          .then((inStock) => {
            if (inStock) {
              this.submitToCart(evt);
            } else {
              this.handleErrorMessage('This product is currently out of stock.');
              this.submitButton.classList.remove('loading');
              this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');
              this.submitButton.disabled = true;
              this.submitButton.textContent = 'SOLD OUT';
            }
          })
          .catch(() => {
            this.handleErrorMessage('Unable to verify stock. Please try again.');
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      submitToCart(evt) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      onDynamicCheckoutClick(evt) {
        const paymentBtn = this._paymentButtonFromEvent(evt);
        if (!paymentBtn) return;
        if (this._dynamicCheckoutAllowed) return;

        evt.preventDefault();
        evt.stopImmediatePropagation();

        const minCart = window.keysouqMinCart;
        if (minCart && typeof minCart.isBelowMinimum === 'function') {
          this.handleErrorMessage();
          if (minCart.isBelowMinimum(this._checkoutTotal())) {
            this.handleErrorMessage(minCart.message);
            return;
          }
        }

        const productId = this.dataset.productId;

        this._fetchStockStatus(productId).then((inStock) => {
          if (inStock) {
            this._dynamicCheckoutAllowed = true;
            paymentBtn.click();
            this._dynamicCheckoutAllowed = false;
          } else {
            this.toggleSubmitButton(true, 'SOLD OUT');
            this._setPaymentButtonsDisabled(true);
            this.handleErrorMessage('This product is currently out of stock.');
          }
        }).catch(() => {
          this.handleErrorMessage('Unable to verify stock. Please try again.');
        });
      }

      /*
       * Buy Now bypasses the cart, so the amount to validate is what this form
       * would check out with: selected variant price in cents x quantity.
       * Returns NaN when the price cannot be read, which isBelowMinimum()
       * treats as "do not block" so a missing price never stops a checkout.
       */
      _checkoutTotal() {
        const price = this._variantPrice();
        return isNaN(price) ? NaN : price * this._quantity();
      }

      /*
       * The quantity selector sits outside the <form> and is tied to it with a
       * `form` attribute, so it is only reachable through form.elements.
       * Forms without one (product cards) check out a single unit.
       */
      _quantity() {
        const field = this.form.elements.namedItem('quantity');
        const quantity = Math.floor(this._toNumber(field && field.value));

        return quantity > 0 ? quantity : 1;
      }

      /*
       * The variant JSON is re-rendered on every variant change, so it stays
       * accurate where a variant picker exists. Product cards and the custom
       * product layout have no picker and fall back to the price rendered into
       * data-variant-price, which is re-rendered along with the form.
       */
      _variantPrice() {
        const variantJSON = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');

        if (variantJSON) {
          try {
            const price = this._toNumber(JSON.parse(variantJSON.textContent).price);
            if (!isNaN(price)) return price;
          } catch (e) {
            /* Fall through to the rendered price. */
          }
        }

        return this._toNumber(this.dataset.variantPrice);
      }

      /* Number(null) and Number('') are 0 while Number(undefined) is NaN; a
       * missing price has to read as NaN every time so it is never mistaken
       * for a free product. */
      _toNumber(value) {
        if (value === null || value === undefined || value === '') return NaN;

        return Number(value);
      }

      _paymentButtonFromEvent(evt) {
        if (!(evt.target instanceof Element)) return null;

        const selector = [
          '.shopify-payment-button__button',
          'shopify-buy-it-now-button button',
          'shopify-accelerated-checkout button',
          '[data-checkout-button]',
        ].join(', ');
        const button = evt.target.closest(selector);

        return button && this.contains(button) ? button : null;
      }

      _paymentButtons() {
        return Array.from(
          this.querySelectorAll(
            '.shopify-payment-button__button, shopify-buy-it-now-button button, shopify-accelerated-checkout button, [data-checkout-button]'
          )
        );
      }

      _setPaymentButtonsDisabled(disabled) {
        this._paymentButtons().forEach((button) => {
          button.toggleAttribute('disabled', disabled);
          button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        });
      }

      async _fetchStockStatus(productId) {
        if (!window.keysouqVerifyStock) return true;
        const r = await fetch('/apps/service', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action_type: 'verify_stock', gids: [productId] }),
        });
        const stockData = await r.json();
        const match = Array.isArray(stockData.data)
          ? stockData.data.find((item) => String(item.guid) === String(productId))
          : null;
        return match ? match.is_in_stock === true : false;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this._setPaymentButtonsDisabled(true);
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this._setPaymentButtonsDisabled(false);
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
